/**
 * @component Header
 * @description Main navigation component that provides site-wide navigation and language selection.
 * Includes responsive mobile menu and services dropdown.
 *
 * @returns {JSX.Element} A header component with navigation menu and language selector
 *
 * @example
 * <Header />
 *
 * @notes
 * - Uses Framer Motion for animations
 * - Implements responsive design for mobile and desktop
 * - Integrates with i18next for translations
 * - Includes dropdown menu for services
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Menu, X, ChevronDown } from 'lucide-react';
import LanguageSelector from './LanguageSelector';
import CartIcon from '../cart/CartIcon';
import { useCart } from '../../contexts/CartContext';

const Header = React.memo(() => {
  const { t } = useTranslation();
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);
  const [isServicesOpen, setIsServicesOpen] = React.useState(false);

  const serviceLinks = [
    { href: '/servicios/impresion-fdm', label: 'common.navigation.fdm' },
    { href: '/servicios/impresion-sla', label: 'common.navigation.sla' },
    { href: '/servicios/diseno-cad', label: 'common.navigation.cad' },
  ];

  const navLinks = [
    { href: '/calculadora', label: 'common.navigation.calculator' },
    { href: '/portafolio', label: 'common.navigation.portfolio' },
    { href: '/blog', label: 'common.navigation.blog' },
    { href: '/contacto', label: 'common.navigation.contact' },
  ];

  const { count } = useCart();
  return (
    <motion.header
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      className="fixed w-full bg-white/95 backdrop-blur-sm z-50 border-b border-gray-100"
    >
      <nav className="container mx-auto px-4 py-4">
        <div className="flex items-center justify-between">
          <Link to="/" className="flex items-center space-x-2">
            <img
              src="/assets/images/logo.png"
              alt="i3d.Ninja - Logo de servicios de impresión 3D y diseño CAD en Monterrey"
              className="h-[17px] md:h-[34px] lg:h-[51px]"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8" data-testid="desktop-nav">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
              className="flex items-center space-x-8"
            >
              <Link
                to="/"
                className="text-gray-700 hover:text-blue-600 transition-colors"
              >
                {t('common.navigation.home')}
              </Link>
              
              {/* Servicios Dropdown */}
              <div
                className="relative"
                onMouseEnter={() => setIsServicesOpen(true)}
                onMouseLeave={() => setIsServicesOpen(false)}
              >
                <button
                  className="flex items-center space-x-1 text-gray-700 hover:text-blue-600 transition-colors"
                >
                  <span>{t('common.navigation.services')}</span>
                  <ChevronDown className={`w-4 h-4 transition-transform ${isServicesOpen ? 'rotate-180' : ''}`} />
                </button>
                <AnimatePresence>
                  {isServicesOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 10 }}
                      className="absolute left-0 mt-2 w-48 bg-white rounded-md shadow-lg py-2 z-50"
                    >
                      {serviceLinks.map((link) => (
                        <Link
                          key={link.href}
                          to={link.href}
                          className="block px-4 py-2 text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition-colors"
                        >
                          {t(link.label)}
                        </Link>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  className="text-gray-700 hover:text-blue-600 transition-colors"
                >
                  {t(link.label)}
                </Link>
              ))}
              {/* Admin Menu removido del header público para ubicarlo solo en el ámbito del dashboard */}
            </motion.div>
            <div className="flex items-center space-x-4">
              <LanguageSelector />
              {count > 0 ? (
                <CartIcon />
              ) : (
                <Link
                  to="/contacto#contact-forms"
                  className="bg-blue-600 text-white px-6 py-2 rounded-full hover:bg-blue-700 transition-colors"
                >
                  {t('common.cta.quote')}
                </Link>
              )}
            </div>
          </div>

          {/* Mobile Navigation */}
          <div className="md:hidden flex items-center space-x-3">
            {/* Mover el selector de idioma a la izquierda del botón del menú */}
            <LanguageSelector />
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="text-gray-700"
              aria-label="Toggle menu"
            >
              {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {isMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="md:hidden"
            >
              <div className="py-4 space-y-4">
                <Link
                  to="/"
                  className="block text-gray-700 hover:text-blue-600 transition-colors"
                  onClick={() => setIsMenuOpen(false)}
                >
                  {t('common.navigation.home')}
                </Link>
                
                {/* Servicios Mobile Dropdown */}
                <div>
                  <button
                    onClick={() => setIsServicesOpen(!isServicesOpen)}
                    className="flex items-center justify-between w-full text-gray-700"
                  >
                    <span>{t('common.navigation.services')}</span>
                    <ChevronDown className={`w-4 h-4 transition-transform ${isServicesOpen ? 'rotate-180' : ''}`} />
                  </button>
                  <AnimatePresence>
                    {isServicesOpen && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        className="pl-4 mt-2 space-y-2"
                      >
                        {serviceLinks.map((link) => (
                          <Link
                            key={link.href}
                            to={link.href}
                            className="block text-gray-600 hover:text-blue-600 transition-colors"
                            onClick={() => setIsMenuOpen(false)}
                          >
                            {t(link.label)}
                          </Link>
                        ))}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                {navLinks.map((link) => (
                  <Link
                    key={link.href}
                    to={link.href}
                    className="block text-gray-700 hover:text-blue-600 transition-colors"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    {t(link.label)}
                  </Link>
                ))}
                {/* Admin Menu removido del header público (mobile) */}
                <div className="pt-4 space-y-4">
                  <Link
                    to="/contacto#contact-forms"
                    className="block bg-blue-600 text-white px-6 py-2 rounded-full hover:bg-blue-700 transition-colors text-center"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    {t('common.cta.quote')}
                  </Link>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>
    </motion.header>
  );
});

export default Header;
